import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, ListChecks } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { TaskRow } from '@/components/tasks/TaskRow';
import { TaskDialog } from '@/components/tasks/TaskDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProjectTasks() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [dialog, setDialog] = useState({ open: false, task: null, stage: '' });

  const load = useCallback(async () => {
    try {
      const [p, t] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/projects/${projectId}`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/projects/${projectId}/tasks`, { withCredentials: true }),
      ]);
      setProject(p.data); setTasks(t.data);
    } catch { toast.error('Failed to load tasks'); } finally { setLoading(false); }
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const byId = useMemo(() => Object.fromEntries(tasks.map(t => [t.task_id, t])), [tasks]);
  const blockersOf = (t) => (t.depends_on || []).map(id => byId[id]).filter(b => b && b.status !== 'Done');
  const stages = useMemo(() => [...new Set(tasks.map(t => t.stage))], [tasks]);
  const done = tasks.filter(t => t.status === 'Done').length;
  const visible = (t) => filter === 'all' || (filter === 'ready' ? t.status !== 'Done' && blockersOf(t).length === 0 : filter === 'done' ? t.status === 'Done' : t.status !== 'Done');

  const toggle = async (task) => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/tasks/${task.task_id}/toggle`, {}, { withCredentials: true });
      setTasks(ts => ts.map(t => t.task_id === task.task_id ? r.data : t));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to update task'); }
  };

  const applyTemplate = async () => {
    try {
      const r = await axios.post(`${BACKEND_URL}/api/projects/${projectId}/tasks/apply-template`, {}, { withCredentials: true });
      toast.success(`${r.data.added} tasks loaded`); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load checklist'); }
  };

  if (loading) return <div className="text-stone-600">Loading tasks...</div>;
  const pct = tasks.length ? Math.round((done / tasks.length) * 100) : 0;

  return (
    <div className="space-y-6 pt-10 lg:pt-0" data-testid="project-tasks-page">
      <button onClick={() => navigate('/admin/projects')} data-testid="tasks-back-btn" className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900">
        <ArrowLeft className="w-4 h-4" /> Projects
      </button>
      <div className="flex flex-wrap justify-between items-end gap-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-amber-800 font-medium">{project?.project_type}</p>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }} data-testid="tasks-project-name">
            {project?.project_name}
          </h1>
        </div>
        <Button onClick={() => setDialog({ open: true, task: null, stage: '' })} data-testid="add-task-btn" className="bg-amber-700 hover:bg-amber-800 text-white">
          <Plus className="w-4 h-4 mr-2" /> Add task
        </Button>
      </div>

      {tasks.length === 0 ? (
        <div className="bg-white border border-dashed border-stone-300 rounded-lg p-10 text-center" data-testid="tasks-empty">
          <ListChecks className="w-10 h-10 mx-auto text-stone-400" strokeWidth={1.5} />
          <p className="mt-3 text-stone-700">No tasks for this project yet.</p>
          <Button onClick={applyTemplate} data-testid="apply-template-btn" className="mt-4 bg-stone-900 hover:bg-stone-800 text-white">
            Load {project?.project_type} checklist
          </Button>
        </div>
      ) : (
        <>
          <div className="bg-white border border-stone-200 rounded-lg p-5">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-stone-600">Progress</span>
              <span className="font-semibold text-stone-900 tabular-nums" data-testid="tasks-progress">{done} / {tasks.length} done · {pct}%</span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="flex gap-1.5 mt-4">
              {[['all', 'All'], ['ready', 'Ready to do'], ['pending', 'Pending'], ['done', 'Done']].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} data-testid={`tasks-filter-${k}`}
                  className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === k ? 'bg-stone-900 text-white border-stone-900' : 'bg-white text-stone-600 border-stone-200 hover:border-stone-400'}`}>{l}</button>
              ))}
            </div>
          </div>
          {stages.map((s, i) => {
            const st = tasks.filter(t => t.stage === s);
            const shown = st.filter(visible);
            if (!shown.length) return null;
            const sd = st.filter(t => t.status === 'Done').length;
            return (
              <section key={s} className="bg-white border border-stone-200 rounded-lg overflow-hidden" data-testid={`stage-${i}`}>
                <header className="flex items-center justify-between px-4 py-3 bg-stone-50 border-b border-stone-200">
                  <div className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full text-[11px] font-semibold flex items-center justify-center ${sd === st.length ? 'bg-lime-700 text-white' : 'bg-stone-200 text-stone-700'}`}>{i + 1}</span>
                    <h2 className="text-sm font-semibold text-stone-900">{s}</h2>
                    <span className="text-xs text-stone-500 tabular-nums">{sd}/{st.length}</span>
                  </div>
                  <button onClick={() => setDialog({ open: true, task: null, stage: s })} data-testid={`stage-add-task-${i}`} className="text-xs text-amber-800 hover:text-amber-900 inline-flex items-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </header>
                {shown.map(t => <TaskRow key={t.task_id} task={t} blockers={blockersOf(t)} onToggle={toggle} onOpen={(task) => setDialog({ open: true, task, stage: '' })} />)}
              </section>
            );
          })}
        </>
      )}

      <TaskDialog open={dialog.open} task={dialog.task} defaultStage={dialog.stage} tasks={tasks} stages={stages}
        projectId={projectId} onSaved={load} onClose={() => setDialog({ open: false, task: null, stage: '' })} />
    </div>
  );
}
